/**
 * Convert Unix timestamp to formatted time string
 * Rules:
 * - "now" if published within the last minute
 * - "X mins ago", "X hours ago" if within 24 hours
 * - "Nov 18, 2025, 03:23 am" format for older dates
 */
export const formatPublishedTime = (unixTimestamp: number): string => {
  const now = Math.floor(Date.now() / 1000); // Current time in seconds
  const secondsAgo = now - unixTimestamp;

  // If less than 1 minute ago, show "now"
  if (secondsAgo < 60) {
    return 'now';
  }

  // If less than 24 hours ago, show relative time
  if (secondsAgo < 86400) {
    const minutesAgo = Math.floor(secondsAgo / 60);
    if (minutesAgo < 60) {
      return `${minutesAgo} min${minutesAgo !== 1 ? 's' : ''} ago`;
    }

    const hoursAgo = Math.floor(secondsAgo / 3600);
    return `${hoursAgo} hour${hoursAgo !== 1 ? 's' : ''} ago`;
  }

  // For older dates, format as "Nov 18, 2025, 03:23 am"
  const date = new Date(unixTimestamp * 1000);

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';

  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12

  // Format minutes with leading zero
  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;

  return `${month} ${day}, ${year}, ${hours
    .toString()
    .padStart(2, '0')}:${minutesStr} ${ampm}`;
};

/**
 * Format date string to "Oct 22, 1:12 PM" format
 */
export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const month = monthNames[date.getMonth()];
  const day = date.getDate();

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12;
  hours = hours ? hours : 12;

  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;

  return `${month} ${day}, ${hours}:${minutesStr} ${ampm}`;
};

export const formatDate = (value?: number | Date | string) => {
  let result = '';
  try {
    result = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    }).format(value as Date);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);

    result = `Invalid Date - ${msg}`;
  }
  return result;
};
